import { ACTIVE_URL } from "@/constants";
import {
  authHeaders,
  clearTokens,
  getRefreshToken,
  setAccessToken,
} from "@/hooks/useAuth";

// Shared in-flight refresh promise so that several requests that 401 at the
// same time only trigger a single /api/token/refresh/ call.
let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const res = await fetch(`${ACTIVE_URL}/api/token/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.access) return false;
    setAccessToken(data.access);
    return true;
  } catch {
    return false;
  }
}

function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function redirectToLogin(): never {
  clearTokens();
  window.location.href = "/admin/login";
  // Stop the calling code path; the redirect unwinds the app anyway.
  throw new Error("Session expired");
}

/**
 * fetch() for authenticated admin requests.
 *
 * Injects the Bearer token, and on a 401/403 transparently tries to refresh
 * the access token once and retry the request. If refresh fails, it clears
 * tokens and hard-redirects to /admin/login.
 *
 * Non-auth responses are returned unchanged, so callers keep their own
 * `if (!res.ok) throw` handling for ordinary errors.
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const withAuth = (i: RequestInit): RequestInit => ({
    ...i,
    headers: { ...(i.headers ?? {}), ...authHeaders() },
  });

  let res = await fetch(input, withAuth(init));

  if (res.status === 401 || res.status === 403) {
    const refreshed = await tryRefresh();
    if (!refreshed) redirectToLogin();
    res = await fetch(input, withAuth(init));
    if (res.status === 401 || res.status === 403) redirectToLogin();
  }

  return res;
}
